declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

class GoogleAnalyticsService {
  private static instance: GoogleAnalyticsService;
  private isEnabled = false;
  private measurementId: string;

  private constructor() {
    this.measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-FE513ZPF6K';
    this.checkGAAvailability();
  }

  public static getInstance(): GoogleAnalyticsService {
    if (!GoogleAnalyticsService.instance) {
      GoogleAnalyticsService.instance = new GoogleAnalyticsService();
    }
    return GoogleAnalyticsService.instance;
  }

  private checkGAAvailability(): void {
    // Check if we're not on localhost and gtag is available
    const isProduction = typeof window !== 'undefined' && 
                        window.location.hostname !== 'localhost' && 
                        window.location.hostname !== '127.0.0.1';
    
    this.isEnabled = isProduction && typeof window !== 'undefined' && typeof window.gtag === 'function';
    
    if (this.isEnabled) {
      console.log('Google Analytics initialized with measurement ID:', this.measurementId);
    } else {
      console.warn('Google Analytics is disabled (localhost or gtag not available)');
    }
  }

  /**
   * Track a custom event
   */
  public trackEvent(event: GAEvent): void {
    if (!this.isEnabled) {
      console.log('GA Event (disabled):', event);
      return;
    }

    try {
      window.gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        ...event.custom_parameters
      });

      console.log('GA Event tracked:', event);
    } catch (error) {
      console.error('Error tracking GA event:', error);
    }
  }

  /**
   * Track page view
   */
  public trackPageView(page_title: string, additional_params?: Record<string, any>): void {
    if (!this.isEnabled) {
      console.log('GA Page View (disabled):', page_title);
      return;
    }

    try {
      const page_location = additional_params?.page_location || window.location.href;
      
      window.gtag('event', 'page_view', {
        page_title,
        page_location,
        ...additional_params
      });

      console.log('GA Page View tracked:', page_title);
    } catch (error) {
      console.error('Error tracking GA page view:', error);
    }
  }

  /**
   * Track button click
   */
  public trackButtonClick(button_name: string, location: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: 'click',
      category: 'button',
      label: `${button_name} - ${location}`,
      custom_parameters: {
        button_name,
        button_location: location,
        ...additional_params
      }
    });
  }

  /**
   * Track section view
   */
  public trackSectionView(section_name: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: 'view',
      category: 'section',
      label: section_name,
      custom_parameters: {
        section_name,
        ...additional_params
      }
    });
  }

  /**
   * Track form submission
   */
  public trackFormSubmission(form_name: string, success: boolean, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: success ? 'submit_success' : 'submit_error',
      category: 'form',
      label: form_name,
      custom_parameters: {
        form_name,
        success,
        ...additional_params
      }
    });
  }

  /**
   * Track video interaction
   */
  public trackVideoInteraction(action: 'play' | 'pause' | 'complete', video_name: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: `video_${action}`,
      category: 'video',
      label: video_name,
      custom_parameters: {
        video_name,
        video_action: action,
        ...additional_params
      }
    });
  }

  /**
   * Track scroll depth
   */
  public trackScrollDepth(depth_percentage: number, section?: string): void {
    // Only track at specific milestones to avoid too many events
    const milestones = [25, 50, 75, 90, 100];
    if (milestones.includes(depth_percentage)) {
      this.trackEvent({
        action: 'scroll_depth',
        category: 'engagement',
        label: `${depth_percentage}%`,
        value: depth_percentage,
        custom_parameters: {
          scroll_depth: depth_percentage,
          section
        }
      });
    }
  }

  /**
   * Track time on page
   */
  public trackTimeOnPage(seconds: number, page_name: string): void {
    // Track time milestones
    const milestones = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
    if (milestones.includes(seconds)) {
      this.trackEvent({
        action: 'time_on_page',
        category: 'engagement',
        label: `${seconds}s`,
        value: seconds,
        custom_parameters: {
          time_seconds: seconds,
          page_name
        }
      });
    }
  }

  /**
   * Track conversion events
   */
  public trackConversion(conversion_type: 'sign_up' | 'trial_request' | 'pricing_view' | 'demo_request' | 'weekend_free_signup' | 'extension_download', additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: conversion_type,
      category: 'conversion',
      label: conversion_type.replace('_', ' '),
      custom_parameters: {
        conversion_type,
        ...additional_params
      }
    });
  }

  /**
   * Track navigation
   */
  public trackNavigation(destination: string, source: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: 'navigate',
      category: 'navigation',
      label: `${source} -> ${destination}`,
      custom_parameters: {
        destination,
        source,
        ...additional_params
      }
    });
  }

  /**
   * Track search
   */
  public trackSearch(search_term: string, results_count?: number): void {
    this.trackEvent({
      action: 'search',
      category: 'search',
      label: search_term,
      value: results_count,
      custom_parameters: {
        search_term,
        results_count
      }
    });
  }

  /**
   * Track feature usage
   */
  public trackFeatureUsage(feature_name: string, action: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: `feature_${action}`,
      category: 'feature',
      label: feature_name,
      custom_parameters: {
        feature_name,
        feature_action: action,
        ...additional_params
      }
    });
  }

  /**
   * Track error
   */
  public trackError(error_message: string, error_location: string, additional_params?: Record<string, any>): void {
    this.trackEvent({
      action: 'error',
      category: 'error',
      label: `${error_location}: ${error_message}`,
      custom_parameters: {
        error_message,
        error_location,
        ...additional_params
      }
    });
  }

  /**
   * Check if GA is enabled
   */
  public isGAEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: Record<string, any>): void {
    if (!this.isEnabled) {
      console.log('GA User Properties (disabled):', properties);
      return;
    }

    try {
      window.gtag('config', this.measurementId, {
        custom_map: properties
      });

      console.log('GA User Properties set:', properties);
    } catch (error) {
      console.error('Error setting GA user properties:', error);
    }
  }
}

// Export singleton instance
export const googleAnalyticsService = GoogleAnalyticsService.getInstance();

// Convenience functions for easy usage
export const trackGAEvent = (event: GAEvent) => {
  googleAnalyticsService.trackEvent(event);
};

export const trackGAPageView = (page_title: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackPageView(page_title, additional_params);
};

export const trackGAButtonClick = (button_name: string, location: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackButtonClick(button_name, location, additional_params);
};

export const trackGASectionView = (section_name: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackSectionView(section_name, additional_params);
};

export const trackGAConversion = (conversion_type: 'sign_up' | 'trial_request' | 'pricing_view' | 'demo_request' | 'weekend_free_signup' | 'extension_download', additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackConversion(conversion_type, additional_params);
};

export const trackGANavigation = (destination: string, source: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackNavigation(destination, source, additional_params);
};

export const trackGAVideoInteraction = (action: 'play' | 'pause' | 'complete', video_name: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackVideoInteraction(action, video_name, additional_params);
};

export const trackGAScrollDepth = (depth_percentage: number, section?: string) => {
  googleAnalyticsService.trackScrollDepth(depth_percentage, section);
};

export const trackGATimeOnPage = (seconds: number, page_name: string) => {
  googleAnalyticsService.trackTimeOnPage(seconds, page_name);
};

export const trackGAFormSubmission = (form_name: string, success: boolean, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackFormSubmission(form_name, success, additional_params);
};

export const trackGAFeatureUsage = (feature_name: string, action: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackFeatureUsage(feature_name, action, additional_params);
};

export const trackGAError = (error_message: string, error_location: string, additional_params?: Record<string, any>) => {
  googleAnalyticsService.trackError(error_message, error_location, additional_params);
}; 